import { DestroyRef, inject, Injectable, NgZone, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, merge, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

/**
 * Context for inactivity monitoring.
 * - 'app': Monitors inactivity at the application level
 * - 'exploration': Monitors inactivity during an exploration
 */
export type InactivityContext = 'app' | 'exploration';

/**
 * Internal state for each inactivity monitoring context
 */
interface InactivityContextState {
  readonly isIdle: ReturnType<typeof signal<boolean>>;
  idleTimeout: number;
  activityTimer?: ReturnType<typeof setTimeout>;
  activitySubscription?: Subscription;
  isObserving: boolean;
}

/**
 * Service to monitor user inactivity based on mouse and keyboard activity.
 * Supports monitoring inactivity at two levels:
 * - App level: For user inactivity detection
 * - Exploration level: For exploration inactivity detection
 *
 * Each context has its own `isIdle` signal that is true when the user has been
 * inactive for the configured timeout for that context.
 */
@Injectable({ providedIn: 'root' })
export class UserInactivityMonitorService {
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly DEFAULT_IDLE_TIMEOUT = 5 * 60 * 1000; // Default idle time: 5 minutes

  /**
   * Map of context states, keyed by InactivityContext
   */
  private readonly contexts = new Map<InactivityContext, InactivityContextState>();

  /**
   * Get or create the state for a given context
   */
  private getContextState(context: InactivityContext): InactivityContextState {
    if (!this.contexts.has(context)) {
      this.contexts.set(context, {
        activitySubscription: undefined,
        activityTimer: undefined,
        idleTimeout: this.DEFAULT_IDLE_TIMEOUT,
        isIdle: signal(false),
        isObserving: false,
      });
    }
    return this.contexts.get(context)!;
  }

  /**
   * Get the idle signal for a specific context
   * @param context The inactivity context ('app' or 'exploration')
   * @returns Signal indicating if the user is idle for the given context
   */
  isIdle(context: InactivityContext = 'app'): Signal<boolean> {
    return this.getContextState(context).isIdle.asReadonly();
  }

  /**
   * Set up idle detection by monitoring user activity events
   * @param context The inactivity context ('app' or 'exploration'). Defaults to 'app'.
   * @param timeout Idle timeout duration in milliseconds
   */
  observeIdleDetection(
    context: InactivityContext = 'app',
    timeout = this.DEFAULT_IDLE_TIMEOUT,
  ): void {
    const state = this.getContextState(context);

    // Prevent multiple registrations for the context
    if (state.isObserving) {
      return;
    }

    state.idleTimeout = timeout;
    state.isObserving = true;

    // Run outside Angular zone for better performance
    this.zone.runOutsideAngular(() => {
      // Monitor various user activity events
      const mouseMove$ = fromEvent(document, 'mousemove');
      const mouseDown$ = fromEvent(document, 'mousedown');
      const keyPress$ = fromEvent(document, 'keypress');
      const scroll$ = fromEvent(document, 'scroll', { capture: true });
      const touchStart$ = fromEvent(document, 'touchstart');
      const click$ = fromEvent(document, 'click');

      // Merge all activity events
      state.activitySubscription = merge(
        mouseMove$,
        mouseDown$,
        keyPress$,
        scroll$,
        touchStart$,
        click$,
      )
        .pipe(
          // Throttle to avoid excessive timer resets, but fire immediately on first event
          throttleTime(1000, undefined, { leading: true, trailing: false }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          // Reset timer immediately when activity is detected
          this.restartIdleTimer(context);
        });

      // Start the initial timer
      this.restartIdleTimer(context);
    });
  }

  /**
   * Unregister idle detection and cleanup resources for a specific context
   */
  unregisterIdleDetection(context: InactivityContext = 'app'): void {
    const state = this.getContextState(context);

    if (!state.isObserving) {
      return;
    }

    // Cleanup activity subscription
    if (state.activitySubscription) {
      state.activitySubscription.unsubscribe();
      state.activitySubscription = undefined;
    }

    // Clear the activity timer
    if (state.activityTimer) {
      clearTimeout(state.activityTimer);
      state.activityTimer = undefined;
    }

    // Reset state
    state.isObserving = false;
    this.zone.run(() => {
      state.isIdle.set(false);
    });
  }

  /**
   * Reset the idle timer when user activity is detected for a specific context
   */
  private restartIdleTimer(context: InactivityContext): void {
    const state = this.getContextState(context);

    if (state.activityTimer) {
      clearTimeout(state.activityTimer);
    }

    this.zone.run(() => {
      state.isIdle.set(false);
    });

    state.activityTimer = setTimeout(() => {
      this.zone.run(() => {
        state.isIdle.set(true);
      });
    }, state.idleTimeout);
  }

  /**
   * Manually reset the idle state and restart the timer for a specific context
   * Only works if idle detection is currently observing for that context
   */
  reset(context: InactivityContext = 'app'): void {
    const state = this.getContextState(context);
    if (state.isObserving) {
      this.restartIdleTimer(context);
    }
  }
}
